import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Loader from '../../../components/Common/Loader';
import { setAutomationFilterQuery, setIsSavedFilterAppliedFilters } from '../../../store/automationFilterSlice';

const AutomationFilterContext = createContext();

export const useAutomationFilters = () => useContext(AutomationFilterContext);

export const AutomationFilterProvider = ({
  children,
  filterStorageKey = 'tempAutomationsFilter',
  pageStorageKey = 'aTPn'
}) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [reloadFilter, setReloadFilter] = useState(true);
  const [savedFilterId, setSavedFilterId] = useState(null);
  const [tempFilters, setTempFilters] = useState({});
  const [filters, setFilters] = useState({});
  useEffect(() => {
    const tempFilter = JSON.parse(localStorage.getItem(filterStorageKey)) || {};
    if (Object.keys(tempFilter).length > 0) {
      dispatch(setAutomationFilterQuery(tempFilter));
      setFilters(tempFilter);
    }
    setIsInitialized(true);
    dispatch(setIsSavedFilterAppliedFilters());

    const clearStoredPage = () => localStorage.removeItem(pageStorageKey);
    window.addEventListener('beforeunload', clearStoredPage);

    return () => window.removeEventListener('beforeunload', clearStoredPage);
  }, [dispatch, filterStorageKey, pageStorageKey]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      if (key == 'page') localStorage.setItem(pageStorageKey, value);
      if ('page' in prev && key != 'page') {
        if (key == 'searchTerm' && (!value || value?.length <= 0)) {
          return { ...prev, [key]: [value], page: [localStorage.getItem(pageStorageKey)] };
        }
        localStorage.setItem(pageStorageKey, prev?.page?.[0]);
        return { ...prev, [key]: [value], page: ['1'] };
      } else {
        return { ...prev, [key]: [value] };
      }
    });
  }, [pageStorageKey]);

  const updateTempFilter = useCallback((list) => {
    setTempFilters((prev) => ({ ...prev, ...list }));
  }, []);

  const applyButtonFilter = useCallback(
    (key, value) => {
      setFilters((prev) => {
        if ('page' in prev && (prev?.page?.[0] > 1 || prev?.page?.[0] != '1')) {
          return { ...prev, ...tempFilters, page: ['1'] };
        } else {
          return { ...prev, ...tempFilters };
        }
      });
    },
    [tempFilters, filters]
  );

  const handleSetSavedFilterId = (id) => {
    setSavedFilterId(id);
  };
  const updateFilterByList = useCallback((list) => {
    setFilters((prev) => ({ ...prev, ...list }));
  }, []);

  const addFilterList = useCallback((list, id = null) => {
    if (id) {
      setSavedFilterId(id);
    }
    setFilters((prev) => list);
  }, []);

  const updateFilterByListTags = useCallback((list) => {
    setFilters((prev) => {
      if (prev?.excludeTags?.length > 0 || prev?.includeTags?.length > 0) {
        return { ...prev, ...list };
      }
      return prev;
    });
  }, []);

  const addFilter = useCallback((key, value) => {
    setFilters((prev) => {
      if ('page' in prev && (prev?.page?.[0] > 1 || prev?.page?.[0] != '1')) {
        return {
          ...prev,
          page: ['1'],
          [key]: prev[key]?.includes(value) ? prev[key].filter((f) => f !== value) : [...(prev[key] || []), value]
        };
      } else {
        return { ...prev, [key]: prev[key]?.includes(value) ? prev[key].filter((f) => f !== value) : [...(prev[key] || []), value] };
      }
    });
  }, []);

  const buildQuery = useCallback(() => {
    return Object.entries(filters)
      ?.map(([key, values]) => {
        // Filter out undefined values before encoding
        const filteredValues = values.filter(value => value !== undefined && value !== null);
        return `${encodeURIComponent(key)}=${filteredValues.map(value => encodeURIComponent(value)).join(',')}`;
      })
      .join('&');
  }, [filters]);

  useEffect(() => {
    const temp = { ...filters };
    if (temp && typeof temp === 'object') {
      delete temp['searchTerm'];
      delete temp['limit'];
      delete temp['endDate'];
      delete temp['startDate'];
    }
    localStorage.setItem(filterStorageKey, JSON.stringify(temp));
  }, [filterStorageKey, filters]);

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <AutomationFilterContext.Provider
      value={{
        filters,
        savedFilterId,
        updateFilter,
        buildQuery,
        updateTempFilter,
        applyButtonFilter,
        handleSetSavedFilterId,
        addFilter,
        updateFilterByList,
        updateFilterByListTags,
        addFilterList,
        setReloadFilter,
        reloadFilter
      }}
    >
      {children}
    </AutomationFilterContext.Provider>
  );
};

export const useQuoteFilter = () => {
  const {
    updateFilter,
    buildQuery,
    handleSetSavedFilterId,
    savedFilterId,
    updateFilterByListTags,
    filters,
    updateTempFilter,
    applyButtonFilter,
    addFilterList,
    addFilter,
    updateFilterByList,
    setReloadFilter,
    reloadFilter
  } = useAutomationFilters();
  return {
    updateFilter,
    buildQuery,
    filters,
    savedFilterId,
    handleSetSavedFilterId,
    addFilter,
    addFilterList,
    updateFilterByListTags,
    updateFilterByList,
    updateTempFilter,
    applyButtonFilter,
    setReloadFilter,
    reloadFilter
  };
};
